import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AlertCircle, Loader2 } from "lucide-react";
import { authApi } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import type { TokenPair } from "@/api/tokens";
import { useAuth } from "@/auth/AuthProvider";
import { RETURN_TO_KEY } from "@/auth/guards";
import { safeReturnTo } from "./LoginPage";

// A Discord code is single-use. React StrictMode (and a remount) runs the
// effect twice, so the exchange is memoized per code for this page load.
const exchanges = new Map<string, Promise<TokenPair>>();

function exchange(code: string, state: string): Promise<TokenPair> {
  let p = exchanges.get(code);
  if (!p) {
    p = authApi.discordCallback(code, state);
    exchanges.set(code, p);
  }
  return p;
}

function describe(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 400) return "Liên kết đăng nhập không hợp lệ hoặc đã hết hạn.";
    if (e.status === 401) return "Discord từ chối xác thực. Hãy thử lại.";
    return e.message;
  }
  return "Không hoàn tất được đăng nhập.";
}

export function AuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const code = params.get("code");
  const state = params.get("state");
  const discordError = params.get("error");
  const [error, setError] = useState<string | null>(
    discordError ? "Bạn đã huỷ đăng nhập hoặc Discord từ chối yêu cầu." : !code || !state ? "Thiếu mã xác thực từ Discord." : null,
  );

  useEffect(() => {
    if (!code || !state || discordError) return;
    let cancelled = false;
    exchange(code, state).then(
      (pair) => {
        if (cancelled) return;
        signIn(pair);
        let next = "/";
        try {
          next = safeReturnTo(sessionStorage.getItem(RETURN_TO_KEY));
          sessionStorage.removeItem(RETURN_TO_KEY);
        } catch {
          // default to overview
        }
        // replace: the one-time code must not stay in history.
        navigate(next, { replace: true });
      },
      (e: unknown) => {
        if (!cancelled) setError(describe(e));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [code, state, discordError, signIn, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-page px-4">
      <div className="w-full max-w-sm rounded-lg border border-line bg-surface p-6 text-center">
        {error ? (
          <>
            <AlertCircle className="mx-auto mb-3 size-6 text-critical-ink" aria-hidden />
            <p className="mb-4 text-sm text-ink-2" role="alert">
              {error}
            </p>
            <Link to="/login" replace className="text-sm font-medium text-accent-text hover:underline">
              Quay lại trang đăng nhập
            </Link>
          </>
        ) : (
          <p className="flex items-center justify-center gap-2 text-sm text-ink-2" role="status">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Đang hoàn tất đăng nhập…
          </p>
        )}
      </div>
    </div>
  );
}
